import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Table from '../ogComponent/table';
import Spinner from '../ogComponent/spinner';
import axios from 'axios';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import { Switch, Modal, Box, Button, Typography } from '@mui/material';

export default function AllAttendance() {
  const [product, setProduct] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [modalOpen, setModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  // Access permissions array from Redux store
  const permissions = useSelector((state) => state.emp.permissions);

  const pagePermissions = ["showattendanceID", "attendancedetailsID", "deleteattendanceID"];

  const header = [
    { name: 'si_no', label: '#', active: true },
    { name: 'employee_code', label: 'ID', active: true },
    { name: 'name', label: 'Name', active: true },
    { name: 'date', label: 'Date', active: true },
    { name: 'clock_in', label: 'Check-In', active: true },
    { name: 'clock_out', label: 'Check-Out', active: true },
    { name: 'working_hours', label: 'Hours', active: true },
    { name: 'action', label: 'Action', active: permissions.includes("deleteattendanceID") },
  ];

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const fetchAttendanceAndLeaveData = async (start, end) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');

      const [attendanceResponse, leaveResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API}/api/attendance/attendance/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: start.format('YYYY-MM-DD'),
            end_date: end.format('YYYY-MM-DD'),
          },
        }),
        axios.get(`${process.env.REACT_APP_API}/api/leavemanagement/leaves/filter_by_date/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: start.format('YYYY-MM-DD'),
            end_date: end.format('YYYY-MM-DD'),
          },
        }),
      ]);

      const attendanceData = attendanceResponse.data.map((item, index) => ({
        ...item,
        si_no: index + 1,
        employee_code: item.employee.employee_code || 'N/A',
        working_hours: item.employee.working_hours || 'N/A',
        name: (
          <td className="d-flex gap-3 align-items-center px-0">
            <img
              src={item.employee.profile_photo || '/assets/user.png'}
              alt="user img"
              style={{ width: '25px', height: '25px', borderRadius: '50%' }}
            />
            <div>{item.employee?.name || 'N/A'}</div>
          </td>
        ),
        date: dayjs(item.date).format('YYYY-MM-DD'),
        clock_in: item.clock_in || 'N/A',
        clock_out: item.clock_out || 'N/A',
        action: permissions.includes("deleteattendanceID") ? (
          <Switch
            checked={item.attendance_leave_status === 'Present'}
            onClick={(e) => handleSwitchClick(e, item)}
          />
        ) : null,
      }));

      const leaveData = leaveResponse.data.map((item, index) => ({
        ...item,
        si_no: attendanceData.length + index + 1,
        employee_code: item.employee.employee_code || 'N/A',
        name: (
          <td className="d-flex gap-3 align-items-center px-0">
            <img
              src={item.employee.profile_photo || '/assets/user.png'}
              alt="user img"
              style={{ width: '25px', height: '25px', borderRadius: '50%' }}
            />
            <div>{item.employee.name || 'N/A'}</div>
          </td>
        ),
        date: dayjs(item.start_date).format('YYYY-MM-DD'),
        clock_in: '-',
        clock_out: '-',
        action: permissions.includes("deleteattendanceID") ? (
          <Switch
            checked={item.attendance_leave_status === 'Present'}
            onClick={(e) => handleSwitchClick(e, item)}
          />
        ) : null,
      }));

      setProduct([...attendanceData, ...leaveData]);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissions.includes("showattendanceID")) {
      fetchAttendanceAndLeaveData(startDate, endDate);
    } else {
      setLoading(false);
    }
  }, [permissions]);

  const handleDateChange = (newStartDate, newEndDate) => {
    if (newStartDate) setStartDate(newStartDate);
    if (newEndDate) setEndDate(newEndDate);

    if (permissions.includes("showattendanceID")) {
      fetchAttendanceAndLeaveData(newStartDate || startDate, newEndDate || endDate);
    }
  };

  const handleRowClick = (item) => {
    if (permissions.includes("attendancedetailsID") || String(item.employee.id) === String(userId)) {
      navigate(`/user/dayList/${item?.employee?.id}`);
    }
  };

  const handleSwitchClick = (e, item) => {
    if (permissions.includes("deleteattendanceID")) {
      e.stopPropagation();
      setCurrentItem(item);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setCurrentItem(null);
  };

  const handleModalConfirm = async () => {
    if (!permissions.includes("deleteattendanceID")) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_API}/api/leavemanagement/leaves/toggle_leave_status/`,
        {
          emp_id: currentItem.employee.id,
          date: currentItem.date || currentItem.start_date,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAttendanceAndLeaveData(startDate, endDate);
    } catch (error) {
      console.error('Error updating attendance status:', error);
    } finally {
      handleModalClose();
    }
  };

  return (
    <div className="AttendanceContainer w-100 h-100 px-3 px-lg-4 pt-3">
      <h3 className="pt-sm-2">All Attendance</h3>
      <div className="accordion-body pb-3 w-100 mt-3 mt-sm-0">
        <div className="d-flex gap-3 align-items-center justify-content-end">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              className="MuidatePicker"
              value={startDate}
              onChange={(newValue) => handleDateChange(newValue, null)}
            />
            <DatePicker
              label="End Date"
              className="MuidatePicker"
              value={endDate}
              onChange={(newValue) => handleDateChange(null, newValue)}
            />
          </LocalizationProvider>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : permissions.includes("showattendanceID") ? (
        product.length > 0 ? (
          <Table
            header={header}
            product={product}
            onRowClick={handleRowClick}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        ) : (
          <div className="d-flex justify-content-center align-items-end position-relative" style={{ height: "50vh" }}>
            <div className="position-absolute top-0 start-0 w-100 h-100" style={{
              backgroundImage: 'url(/assets/file.png)',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.3,
            }}></div>
            <p className="m-0 fw-light fs-5 position-relative">No Attendance Today!</p>
          </div>
        )
      ) : (
        <p>No permission to view attendance.</p>
      )}

      {modalOpen && permissions.includes("deleteattendanceID") && (
        <Modal open={modalOpen} onClose={handleModalClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 300,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" component="h2">
              {currentItem?.attendance_leave_status === 'Present'
                ? 'Change attendance to Leave?'
                : 'Change attendance to Present?'}
            </Typography>
            <Box mt={2} display="flex" justifyContent="space-between">
              <Button variant="outlined" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleModalConfirm}>
                OK
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </div>
  );
}